import type { CrmTranslate } from './crm-copy';

export function createCrmImportCopy(t: CrmTranslate) {
  return {
    importPage: {
      documentTitle: t('crm.importPage.documentTitle'),
      title: t('crm.importPage.title'),
      actions: {
        backToContacts: t('crm.importPage.actions.backToContacts'),
        downloadTemplate: t('crm.importPage.actions.downloadTemplate'),
        back: t('crm.importPage.actions.back'),
        importContacts: t('crm.importPage.actions.importContacts'),
        viewContacts: t('crm.importPage.actions.viewContacts'),
        importAnotherFile: t('crm.importPage.actions.importAnotherFile'),
        dismissError: t('crm.importPage.actions.dismissError'),
      },
      steps: {
        step: t('crm.importPage.steps.step'),
        ofFour: t('crm.importPage.steps.ofFour'),
        upload: t('crm.importPage.steps.upload'),
        mapColumns: t('crm.importPage.steps.mapColumns'),
        import: t('crm.importPage.steps.import'),
        results: t('crm.importPage.steps.results'),
      },
      upload: {
        title: t('crm.importPage.upload.title'),
        description: t('crm.importPage.upload.description'),
        dropTitle: t('crm.importPage.upload.dropTitle'),
        supportText: t('crm.importPage.upload.supportText'),
        needTemplate: t('crm.importPage.upload.needTemplate'),
      },
      mapping: {
        title: t('crm.importPage.mapping.title'),
        description: t('crm.importPage.mapping.description'),
        rowsDetected: (count: string) =>
          t('crm.importPage.mapping.rowsDetected', {
            values: { count },
          }),
        csvColumn: t('crm.importPage.mapping.csvColumn'),
        sampleData: t('crm.importPage.mapping.sampleData'),
        mapsTo: t('crm.importPage.mapping.mapsTo'),
        createCompanies: t('crm.importPage.mapping.createCompanies'),
        storeSourceRows: t('crm.importPage.mapping.storeSourceRows'),
      },
      picker: {
        skipColumn: t('crm.importPage.picker.skipColumn'),
        searchFields: t('crm.importPage.picker.searchFields'),
        noFieldsMatch: (query: string) =>
          t('crm.importPage.picker.noFieldsMatch', {
            values: { query },
          }),
      },
      fields: {
        firstName: t('crm.importPage.fields.firstName'),
        middleName: t('crm.importPage.fields.middleName'),
        lastName: t('crm.importPage.fields.lastName'),
        salutation: t('crm.importPage.fields.salutation'),
        suffix: t('crm.importPage.fields.suffix'),
        email: t('crm.importPage.fields.email'),
        directPhone: t('crm.importPage.fields.directPhone'),
        mobilePhone: t('crm.importPage.fields.mobilePhone'),
        companyName: t('crm.importPage.fields.companyName'),
        jobTitle: t('crm.importPage.fields.jobTitle'),
        jobFunction: t('crm.importPage.fields.jobFunction'),
        seniority: t('crm.importPage.fields.seniority'),
        department: t('crm.importPage.fields.department'),
        educationLevel: t('crm.importPage.fields.educationLevel'),
        externalId: t('crm.importPage.fields.externalId'),
        externalContactProfileUrl: t('crm.importPage.fields.externalContactProfileUrl'),
        leadScore: t('crm.importPage.fields.leadScore'),
        contactAccuracyGrade: t('crm.importPage.fields.contactAccuracyGrade'),
        emailVerificationStatus: t('crm.importPage.fields.emailVerificationStatus'),
        emailVerificationProvider: t('crm.importPage.fields.emailVerificationProvider'),
        emailVerifiedAt: t('crm.importPage.fields.emailVerifiedAt'),
        emailDomainMatch: t('crm.importPage.fields.emailDomainMatch'),
        enrichmentSource: t('crm.importPage.fields.enrichmentSource'),
        enrichmentBatchId: t('crm.importPage.fields.enrichmentBatchId'),
        sourceConfidence: t('crm.importPage.fields.sourceConfidence'),
        contactAddress: t('crm.importPage.fields.contactAddress'),
        contactCity: t('crm.importPage.fields.contactCity'),
        contactStateRegion: t('crm.importPage.fields.contactStateRegion'),
        contactPostalCode: t('crm.importPage.fields.contactPostalCode'),
        contactCountry: t('crm.importPage.fields.contactCountry'),
        linkedInContactUrl: t('crm.importPage.fields.linkedInContactUrl'),
        contactWebsiteUrl: t('crm.importPage.fields.contactWebsiteUrl'),
        companyDomain: t('crm.importPage.fields.companyDomain'),
        companySizeSnapshot: t('crm.importPage.fields.companySizeSnapshot'),
        companyExternalId: t('crm.importPage.fields.companyExternalId'),
        companyExternalProfileUrl: t('crm.importPage.fields.companyExternalProfileUrl'),
        companyWebsite: t('crm.importPage.fields.companyWebsite'),
        companyPhone: t('crm.importPage.fields.companyPhone'),
        companyFax: t('crm.importPage.fields.companyFax'),
        companyFoundedYear: t('crm.importPage.fields.companyFoundedYear'),
        companyTicker: t('crm.importPage.fields.companyTicker'),
        companyEmployeeCount: t('crm.importPage.fields.companyEmployeeCount'),
        companyEmployeeRange: t('crm.importPage.fields.companyEmployeeRange'),
        companyRevenueRange: t('crm.importPage.fields.companyRevenueRange'),
        companyIndustry: t('crm.importPage.fields.companyIndustry'),
        companySubIndustry: t('crm.importPage.fields.companySubIndustry'),
        companyLinkedInUrl: t('crm.importPage.fields.companyLinkedInUrl'),
        companyStreetAddress: t('crm.importPage.fields.companyStreetAddress'),
        companyCity: t('crm.importPage.fields.companyCity'),
        companyStateRegion: t('crm.importPage.fields.companyStateRegion'),
        companyPostalCode: t('crm.importPage.fields.companyPostalCode'),
        companyCountry: t('crm.importPage.fields.companyCountry'),
        tags: t('crm.importPage.fields.tags'),
      },
      importing: {
        title: t('crm.importPage.importing.title'),
        processingContacts: (count: string) =>
          t('crm.importPage.importing.processingContacts', {
            values: { count },
          }),
        largeFileDetected: t('crm.importPage.importing.largeFileDetected'),
        processingFileInBatches: (sizeMb: number) =>
          t('crm.importPage.importing.processingFileInBatches', {
            values: { sizeMb },
          }),
        keepPageOpen: t('crm.importPage.importing.keepPageOpen'),
      },
      results: {
        title: t('crm.importPage.results.title'),
        contactsImported: t('crm.importPage.results.contactsImported'),
        companiesCreated: t('crm.importPage.results.companiesCreated'),
        failed: t('crm.importPage.results.failed'),
        processedContacts: (count: string) =>
          t('crm.importPage.results.processedContacts', {
            values: { count },
          }),
        fromFile: (sizeMb: number) =>
          t('crm.importPage.results.fromFile', {
            values: { sizeMb },
          }),
        autoCreatedCompanies: (count: string) =>
          t('crm.importPage.results.autoCreatedCompanies', {
            values: { count },
          }),
        errors: t('crm.importPage.results.errors'),
        row: (row: number) =>
          t('crm.importPage.results.row', {
            values: { row },
          }),
        moreErrors: (count: number) =>
          t('crm.importPage.results.moreErrors', {
            values: { count },
          }),
      },
      meta: {
        rowsDetected: (count: string) =>
          t('crm.importPage.meta.rowsDetected', {
            values: { count },
          }),
        fileSize: (sizeMb: number) =>
          t('crm.importPage.meta.fileSize', {
            values: { sizeMb },
          }),
      },
      errors: {
        previewFailed: t('crm.importPage.errors.previewFailed'),
        processFailed: t('crm.importPage.errors.processFailed'),
        csvRequired: t('crm.importPage.errors.csvRequired'),
        importFailed: t('crm.importPage.errors.importFailed'),
        importTimedOut: t('crm.importPage.errors.importTimedOut'),
        templateDownloadFailed: (error: string) =>
          t('crm.importPage.errors.templateDownloadFailed', {
            values: { error },
          }),
        unknownError: t('crm.importPage.errors.unknownError'),
        fileTooLarge: (sizeMb: number) =>
          t('crm.importPage.errors.fileTooLarge', {
            values: { sizeMb },
          }),
        processingLargeFile: (sizeMb: number) =>
          t('crm.importPage.errors.processingLargeFile', {
            values: { sizeMb },
          }),
        templateDownloadConsole: t('crm.importPage.errors.templateDownloadConsole'),
        refreshDataConsole: t('crm.importPage.errors.refreshDataConsole'),
      },
    },
  };
}
